import React from 'react';

import { ServerListResponse } from '@lumapps/base-api';
import { useInfiniteQuery, useQueryClient } from '@lumapps/base-api/react-query';
import { BaseLoadingStatus } from '@lumapps/utils/types/BaseLoadingStatus';

import { communitiesQueryKeys, list } from '../api';
import { CommunitiesListRequestParams, Community } from '../types';

export interface UseCommunities {
    communities: Community[];
    isLoading: boolean;
    isLoadingMore: boolean;
    hasMoreResults: boolean;
    hasLoadingError: boolean;
    fetch: () => void;
    fetchMore: () => void;
    onSearch: (searchText: string) => void;
    reset: () => void;
    status: BaseLoadingStatus;
}

export interface UseCommunitiesOptions {
    params?: Partial<CommunitiesListRequestParams>;
    fetchOnMount?: boolean;
}

export const useCommunities = ({ params, fetchOnMount }: UseCommunitiesOptions): UseCommunities => {
    const [isEnabled, setIsEnabled] = React.useState(Boolean(fetchOnMount));
    const [searchQuery, setSearchQuery] = React.useState<string | undefined>();

    const queryClient = useQueryClient();

    const queryParams = React.useMemo(() => ({ ...params, query: searchQuery }), [params, searchQuery]);

    const query = useInfiniteQuery<ServerListResponse<Community>>({
        queryKey: communitiesQueryKeys.list(queryParams),
        queryFn: async ({ pageParam }) => {
            const { data } = await list({ ...queryParams, cursor: pageParam });
            return data as ServerListResponse<Community>;
        },
        getNextPageParam: (lastPage) => (lastPage?.more ? lastPage?.cursor : undefined),
        enabled: isEnabled,
    });

    const fetch = React.useCallback(() => {
        setIsEnabled(true);
    }, []);

    const onSearch = React.useCallback((searchText: string) => {
        setSearchQuery(searchText === '' ? undefined : searchText);
        setIsEnabled(true);
    }, []);

    const reset = React.useCallback(() => {
        queryClient.removeQueries({ queryKey: communitiesQueryKeys.all() });
        setIsEnabled(Boolean(fetchOnMount));
        setSearchQuery(undefined);
    }, [queryClient, fetchOnMount]);

    return {
        communities: query.items,
        isLoading: query.isFetching && !query.isFetchingNextPage,
        isLoadingMore: query.isFetchingNextPage,
        hasMoreResults: Boolean(query.hasNextPage),
        hasLoadingError: query.isError,
        fetch,
        fetchMore: query.fetchNextPage,
        onSearch,
        reset,
        status: query.baseLoadingStatus,
    };
};
